import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import { Field, Form } from 'formik';
import Select from 'react-select';

import Link from '../../../../../shared/_TBComponents/Link';
import SubText from '../../../../../shared/_TBComponents/SubText';
import Text from '../../../../../shared/_TBComponents/Text';
import useReseller from '../../hooks/useReseller';

const BILLING_FREQUENCY_OPTIONS = [
  {
    id: 1,
    name: 'Monthly',
  },
  {
    id: 3,
    name: '3 Months',
  },
  {
    id: 6,
    name: '6 Months',
  },
  {
    id: 12,
    name: 'Yearly',
  },
];

const ONE_TIME_CHARGE_OPTION = {
  id: 0,
  name: 'One Time Charge',
};

const TRIAL_PERIOD_MAX_DAYS = 30;
const BILLING_CYCLE_MAX = 12;

const AltFormGroup = styled('div')`
  width: 100%;

  .control {
    border-bottom-right-radius: 0px;
    border-top-right-radius: 0px;
  }

  .alt-form-control {
    min-height: 30px;
    width: 100%;

    div {
      border-bottom-right-radius: 0px;
      border-top-right-radius: 0px;
    }
  }

  .alt-form-control-keep-border {
    min-height: 30px;
    width: 100%;
  }

  .first-stage-select {
    width: 100%;
  }

  .inner-form-group-wrapper {
    display: flex;
    width: 100%;

    .btow {
      border: 1px solid lightgrey;
      border-left: none;
      border-radius: 0.25rem;
      border-bottom-left-radius: 0px;
      border-top-left-radius: 0px;
      font-size: 12px;
      font-weight: 800;
      height: 100%;
      max-height: 38px;
      padding: 9px;
      padding-left: 15px;
      text-align: center;
      width: 30%;

      span {
        margin-left: -5px;
      }
    }
  }

  .inner-form-group-wrapper-keep-border {
    display: flex;
    width: 100%;

    .btow {
      border: 1px solid #ced4da;
      border-radius: 0.25rem;
      font-size: 12px;
      font-weight: 800;
      height: 100%;
      max-height: 38px;
      padding: 9px;
      padding-left: 15px;
      text-align: center;
      width: 30%;

      span {
        margin-left: -5px;
      }
    }
  }
`;

const AnotherWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const CheckboxWrapper = styled('div')`
  padding-top: 12px;
`;

const FieldError = styled('div')`
  color: ${(props) => props.theme.colors.error || '#ff6600'};
  font-size: 14px;
  margin-top: 5px;
`;

const FirstStageInnerWrapper1 = styled('div')`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 55%;
`;

const FirstStageInnerWrapper2 = styled('div')`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 41%;
`;

const FirstStageWrapper = styled('div')`
  align-items: center;
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const FormGroup = styled('div')`
  width: 100%;

  label {
    width: 100%;
  }

  button {
    margin-bottom: 20px;
  }

  .easy-join {
    margin-bottom: 15px;
  }

  .caveat-text {
    font-size: 11px;
    margin-top: 5px;
  }

  @media screen and (max-width: 600px) {
    a {
      font-size: 11px;
    }
  }

  .form-control {
    min-height: 30px;
    width: 100%;
  }

  .alt-form-control {
    background-color: #fff;
    border: 1px solid #ced4da;
    border-radius: 0.25rem;
    border-bottom-left-radius: 0px;
    border-top-left-radius: 0px;
    color: #495057;
    display: block;
    font-size: 1rem;
    font-weight: 400;
    height: calc(1.5em + 0.75rem + 2px);
    line-height: 1.5;
    min-height: 30px;
    padding: 0.375rem 0.75rem;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
    width: 100%;
  }

  .first-stage-select {
    width: 100%;
  }

  .checkbox-label {
    display: flex;
    margin-top: 10px;
  }

  .checkbox {
    margin-right: 10px;
    margin-top: 3px;
  }

  &.prices {
    display: flex;
    justify-content: space-between;
    margin-top: 10px;
  }

  &.fee-plus-label {
    height: 20px;
  }

  &.prices-header {
    margin-top: 15px;
  }

  &.last-line-item {
    padding-bottom: 25px;
  }
`;

const InnerFolderFormWrapper = styled('div')`
  align-items: center;
  display: flex;
  justify-content: left;
  margin-top: 5px;
  width: 80%;

  .formik-form {
    width: 100%;
  }
`;

const ModalWrapper = styled('div')`
  display: flex;
  width: 100%;

  .modal-button {
    align-items: center;
    display: flex;
  }
`;

const NumBillingCyclesWrapper = styled('div')`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  width: 41%;
`;

const OuterFlexWrapper = styled('div')`
  align-items: center;
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const OuterFolderFormWrapper = styled('div')`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  width: 100%;

  .divider-line {
    border-top: 2px solid rgb(232, 232, 232);
    display: flex;
    margin-bottom: 10px;
    margin-top: 5px;
    width: calc(100% + 80px);
  }
`;

const PriceBreakdownContainer = styled('div')`
  background-image: url('data:image/svg+xml;utf8, <svg viewBox="0 0 200 110" xmlns="http://www.w3.org/2000/svg"><path d="M -15 110 L100 10 L215 110" fill="none" stroke="%23ced4da" stroke-width="1" vector-effect="non-scaling-stroke"/></svg>');
  background-position: bottom left;
  background-repeat: repeat-x;
  background-size: 5% auto;
  border: 1px #ced4da solid;
  border-bottom: 0;
  border-top-left-radius: 0.25rem;
  border-top-right-radius: 0.25rem;
  margin-top: 15px;
`;

const PriceBreakdownContents = styled('div')`
  margin: 15px 20px 20px 20px;
`;

const TitlePlusErrorWrapper = styled('div')`
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const TitlePlusErrorWrapper2 = styled('div')`
  align-items: center;
  display: flex;
  justify-content: space-between;
  min-height: 39px;
  width: 100%;
`;

const TrialPeriodWrapper = styled('div')`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  width: 55%;
`;

const CreateProgramSlide3 = ({ formProps, page3Errors }) => {
  const theme = useTheme();
  const { currentReseller } = useReseller();

  const hasSavedWorkouts = formProps.values.savedWorkoutIds?.length > 0;
  const billingFrequencyOptions = hasSavedWorkouts
    ? [ONE_TIME_CHARGE_OPTION]
    : BILLING_FREQUENCY_OPTIONS;

  const trialPeriodOptions = Array.from(
    { length: TRIAL_PERIOD_MAX_DAYS },
    (_, i) => ({
      id: i + 1,
      name: i + 1,
    }),
  );

  const billingCycleOptions = Array.from(
    { length: BILLING_CYCLE_MAX },
    (_, i) => ({
      id: i + 1,
      name: i + 1,
    }),
  );

  const isOneTimeCharge = formProps.values.billingFrequency === 0;

  const padNumber = (num) => parseFloat(num).toFixed(2);

  const calculateStripeFee = () => padNumber(formProps.values.price * 0.029 + 0.3);

  const calculateTeamBuildrFee = () => {
    const feePercentage = currentReseller?.applicationFeePercentage || 0;
    return padNumber(formProps.values.price * (feePercentage / 100));
  };

  const hasApplicationFee = () => (currentReseller?.applicationFeePercentage ?? 0) > 0;

  const formatCurrency = (amount) => {
    const formatted = padNumber(amount);
    if (currentReseller?.placeSymbolAfterCurrency) {
      return `${formatted}${currentReseller?.currencySymbol}`;
    }
    return `${currentReseller?.currencySymbol}${formatted}`;
  };

  const formatCurrencyNegative = (amount) => {
    const formatted = padNumber(amount);
    if (currentReseller?.placeSymbolAfterCurrency) {
      return `-${formatted}${currentReseller?.currencySymbol}`;
    }
    return `-${currentReseller?.currencySymbol}${formatted}`;
  };

  const getTrialAndBillingText = () => {
    const {
      trialPeriod,
      numBillingCycles,
      billingFrequency,
    } = formProps.values;

    let part1;
    if (trialPeriod?.id > 0) {
      const days = trialPeriod.id;
      part1 = `Customer will be charged after ${days} ${
        days > 1 ? 'days' : 'day'
      }`;
    } else {
      part1 = 'Customer will be charged immediately after sign up';
    }

    let part2;
    if (numBillingCycles) {
      let billingFrequencyLabel = 'month';
      let billingMultiplier = billingFrequency;
      if (billingFrequency === 12) {
        billingFrequencyLabel = 'year';
        billingMultiplier = 1;
      }
      const totalCycles = numBillingCycles.id * billingMultiplier;
      part2 = `, and the subscription will end after ${totalCycles} ${billingFrequencyLabel}${
        numBillingCycles.id > 1 ? 's.' : '.'
      }`;
    } else {
      part2 = ', and the subscription will be on-going.';
    }

    return `${part1}${part2}`;
  };

  const handleBillingFrequencyChange = (options) => {
    if (options) {
      formProps.setFieldValue('billingFrequency', options.id);

      if (options.id === 0) {
        formProps.setFieldValue('trialPeriod', null);
        formProps.setFieldValue('numBillingCycles', null);
      }
    }
  };

  const handlePriceBlur = () => {
    if (formProps.values.price) {
      const formattedPrice = parseFloat(formProps.values.price).toFixed(2);
      formProps.setFieldValue('price', formattedPrice);
      formProps.setFieldTouched('price', true);
    }
  };

  const handleTrialPeriodChange = (options) => {
    formProps.setFieldValue('trialPeriod', options);
  };

  const handleBillingCyclesChange = (options) => {
    formProps.setFieldValue('numBillingCycles', options);
  };

  const handleAgreementChange = () => {
    formProps.setFieldValue('agreement', !formProps.values.agreement);
  };

  const getPricePlaceholder = () => {
    if (currentReseller?.placeSymbolAfterCurrency) {
      return `e.g. 50.00${currentReseller?.currencySymbol}`;
    }
    return `e.g. ${currentReseller?.currencySymbol}50.00`;
  };

  const getSelectedTrialPeriod = () => {
    if (!formProps.values.trialPeriod) {
      return null;
    }
    return trialPeriodOptions.find(
      (option) => option.id === formProps.values.trialPeriod.id,
    );
  };

  const getSelectedBillingCycle = () => {
    if (!formProps.values.numBillingCycles) {
      return null;
    }
    return billingCycleOptions.find(
      (option) => option.id === formProps.values.numBillingCycles.id,
    );
  };

  const getSelectedBillingFrequency = () => billingFrequencyOptions.find(
    (option) => option.id === formProps.values.billingFrequency,
  ) || billingFrequencyOptions[0];

  const calculateNetRevenue = () => (
    formProps.values.price - calculateStripeFee() - calculateTeamBuildrFee()
  );

  const formGroupTextProps = { marginBottom: '5px', marginTop: '10px' };
  return (
    <ModalWrapper>
      <AnotherWrapper>
        <OuterFolderFormWrapper>
          <InnerFolderFormWrapper>
            <Form className='formik-form'>
              <FormGroup>
                <FirstStageWrapper>
                  <FirstStageInnerWrapper1>
                    <TitlePlusErrorWrapper2>
                      <Text
                        styledTextProps={{ fontWeight: 700 }}
                        styledWrapperProps={formGroupTextProps}
                      >
                        Billing Frequency
                      </Text>
                      {formProps.errors.billingFrequency
                        && (formProps.touched.billingFrequency || page3Errors) && (
                          <FieldError className='error-text'>
                            {formProps.errors.billingFrequency}
                          </FieldError>
                      )}
                    </TitlePlusErrorWrapper2>
                    <Select
                      className='first-stage-select'
                      defaultValue={getSelectedBillingFrequency()}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.id}
                      isDisabled={hasSavedWorkouts}
                      isSearchable
                      options={billingFrequencyOptions}
                      onChange={handleBillingFrequencyChange}
                    />
                  </FirstStageInnerWrapper1>
                  <FirstStageInnerWrapper2>
                    <FormGroup>
                      <TitlePlusErrorWrapper>
                        <Text
                          styledTextProps={{ fontWeight: 700 }}
                          styledWrapperProps={formGroupTextProps}
                        >
                          Price
                        </Text>
                        {formProps.errors.price
                          && (formProps.touched.price || page3Errors) && (
                            <FieldError className='error-text'>
                              {formProps.errors.price}
                            </FieldError>
                        )}
                      </TitlePlusErrorWrapper>
                      <Field
                        className='form-control'
                        id='price-field'
                        name='price'
                        placeholder={getPricePlaceholder()}
                        type='number'
                        as='select'
                        onBlur={handlePriceBlur}
                      />
                    </FormGroup>
                  </FirstStageInnerWrapper2>
                </FirstStageWrapper>
              </FormGroup>
              {!isOneTimeCharge && (
                <AltFormGroup>
                  <OuterFlexWrapper>
                    <TrialPeriodWrapper>
                      <TitlePlusErrorWrapper2>
                        <Text
                          styledTextProps={{ fontWeight: 700 }}
                          styledWrapperProps={formGroupTextProps}
                        >
                          Trial Period
                        </Text>
                        {formProps.errors.trialPeriod && (
                          <FieldError className='error-text'>
                            {formProps.errors.trialPeriod}
                          </FieldError>
                        )}
                      </TitlePlusErrorWrapper2>
                      <div className='inner-form-group-wrapper'>
                        <Select
                          className='alt-form-control'
                          defaultValue={getSelectedTrialPeriod()}
                          getOptionLabel={(option) => option.name}
                          getOptionValue={(option) => option.id}
                          isClearable
                          isOptionSelected={
                            (trialPeriod) => formProps.values.trialPeriod === trialPeriod
                          }
                          options={trialPeriodOptions}
                          placeholder='N/A'
                          onChange={handleTrialPeriodChange}
                        />
                        <div className='btow'>
                          <SubText>Days</SubText>
                        </div>
                      </div>
                    </TrialPeriodWrapper>
                    <NumBillingCyclesWrapper>
                      <TitlePlusErrorWrapper2>
                        <Text
                          styledTextProps={{ fontWeight: 700 }}
                          styledWrapperProps={formGroupTextProps}
                        >
                          Max Billing Cycles
                        </Text>
                        {formProps.errors.numBillingCycles && (
                          <FieldError className='error-text'>
                            {formProps.errors.numBillingCycles}
                          </FieldError>
                        )}
                      </TitlePlusErrorWrapper2>
                      <div className='inner-form-group-wrapper-keep-border'>
                        <Select
                          className='alt-form-control-keep-border'
                          defaultValue={getSelectedBillingCycle()}
                          getOptionLabel={(option) => option.name}
                          getOptionValue={(option) => option.id}
                          isClearable
                          isOptionSelected={(numBillingCycles) => formProps.values.numBillingCycles
                            === numBillingCycles}
                          options={billingCycleOptions}
                          placeholder='N/A'
                          onChange={handleBillingCyclesChange}
                        />
                      </div>
                    </NumBillingCyclesWrapper>
                  </OuterFlexWrapper>
                </AltFormGroup>
              )}
              {!isOneTimeCharge && (
                <FormGroup>
                  <Text
                    styledTextProps={{ fontSize: '14px' }}
                    styledWrapperProps={{ marginTop: '5px' }}
                  >
                    {getTrialAndBillingText()}
                  </Text>
                </FormGroup>
              )}
              {formProps.values.price && (
                <div>
                  <FormGroup className='prices-header'>
                    <Text
                      styledTextProps={{
                        fontWeight: 600,
                        fontSize: theme.textFontSizes[1],
                      }}
                    >
                      Pricing Breakdown
                    </Text>
                    <Text
                      styledTextProps={{ fontSize: theme.textFontSizes[0] }}
                    >
                      This is how much you make when the program is purchased
                    </Text>
                  </FormGroup>
                  <PriceBreakdownContainer>
                    <PriceBreakdownContents>
                      <FormGroup className='prices'>
                        <SubText>Program Price</SubText>
                        <Text
                          styledTextProps={{
                            fontWeight: 600,
                            fontSize: '13px',
                          }}
                        >
                          {formatCurrency(formProps.values.price)}
                        </Text>
                      </FormGroup>
                      <FormGroup className='prices fee-plus-label'>
                        <SubText>Credit Card Processing Fee</SubText>
                        <Text
                          styledTextProps={{
                            fontWeight: 600,
                            fontSize: '13px',
                          }}
                        >
                          {formatCurrencyNegative(calculateStripeFee())}
                        </Text>
                      </FormGroup>
                      <Link
                        color='lightText.1'
                        display='block'
                        href='https://support.teambuildr.com/article/d2cynfitju-what-are-the-stripe-transaction-charges'
                        rel='noopener noreferrer'
                        target='_blank'
                      >
                        Based on US fee (2.9% + $0.30) - click here for other
                        countries
                      </Link>
                      {hasApplicationFee() && (
                        <FormGroup className='prices'>
                          <SubText>
                            {`TeamBuildr Fee (${currentReseller?.applicationFeePercentage}%)`}
                          </SubText>
                          <Text
                            styledTextProps={{
                              fontWeight: 600,
                              fontSize: '13px',
                            }}
                          >
                            {formatCurrencyNegative(calculateTeamBuildrFee())}
                          </Text>
                        </FormGroup>
                      )}
                      <FormGroup className='prices last-line-item'>
                        <SubText>You Make</SubText>
                        <Text
                          styledTextProps={{
                            color: theme.colors.green,
                            fontWeight: 600,
                            fontSize: '13px',
                          }}
                        >
                          {formatCurrency(calculateNetRevenue())}
                        </Text>
                      </FormGroup>
                    </PriceBreakdownContents>
                  </PriceBreakdownContainer>
                  {formProps.values.billingFrequency === 0 && (
                    <Text
                      styledTextProps={{ fontSize: theme.textFontSizes[0], fontStyle: 'italic' }}
                    >
                      Note: At this time, coupons cannot be applied to
                      one-time charges.
                    </Text>
                  )}
                </div>
              )}
              <FormGroup className='d-flex'>
                <CheckboxWrapper>
                  {formProps.errors.agreement && page3Errors && (
                    <FieldError className='error-text'>
                      {formProps.errors.agreement}
                    </FieldError>
                  )}
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label className='checkbox-label' htmlFor='agreement-field'>
                    <Field
                      className='checkbox'
                      checked={formProps.values.agreement}
                      id='agreement-field'
                      name='agreement'
                      type='checkbox'
                      onChange={handleAgreementChange}
                    />
                    <Text styledTextProps={{ fontSize: '14px' }}>
                      I verify this workout program is my intellectual property
                    </Text>
                  </label>
                </CheckboxWrapper>
              </FormGroup>
            </Form>
          </InnerFolderFormWrapper>
        </OuterFolderFormWrapper>
      </AnotherWrapper>
    </ModalWrapper>
  );
};

CreateProgramSlide3.propTypes = {
  formProps: PropTypes.instanceOf(Object).isRequired,
  page3Errors: PropTypes.bool.isRequired,
};

export default CreateProgramSlide3;
